import { useState } from 'react';
import { Button } from '@clay-run/glaze/button';
import { Icon } from '@clay-run/glaze/icon';
import { SegmentedControl, SegmentedControlOption } from '@clay-run/glaze/segmented-control';
import { SkeletonText } from '@clay-run/glaze/skeleton';
import { Strong, Text } from '@clay-run/glaze/text';

import { FLOW_ICONS } from '../../icons';
import { useCopiedFlash } from '../../../../hooks/useCopiedFlash';
import { useEnrollQueue } from '../../EnrollQueue/EnrollQueueContext';
import { extractDisplayPayload } from '../../EnrollQueue/extractDisplayPayload';
import {
  RequestStatusBadge,
  RequestAttemptStatusBadge,
  extractResponseCode,
  isOutcomeUnavailable,
  formatAbsoluteDateTime,
} from '../../EnrollQueue/request-format';
import { useFlowRequestDetail } from '../../EnrollQueue/requests-hooks';
import { useReenroll, appliedOverlayFor } from '../../EnrollQueue/request-detail-shared';
import {
  requestPathFromFeedEntry,
  routeIdFromFeedEntry,
  type RequestDetail as RequestDetailPayload,
  type RequestEntry,
} from '@shared/flow/requests';
import { EmptyState } from '../ui/EmptyState';
import { Module } from '../ui/Module';
import { FactModule, type FactMode } from './FactModule';

/**
 * ActionsDetail (U12, V2 rail) — the Glaze-styled replacement for V1's
 * `EnrollQueue/RequestDetail.tsx` inside the new Actions surface
 * (`rail/ActionsIndex.tsx`, U11; the switcher between the two is U14's
 * `RailDock` wiring). Same data contract and mutation logic as V1 — ported,
 * not re-derived — new JSX only, matching the approved `sc-terra-flow`
 * prototype's shape (`ActionsDetail.tsx`) rather than V1's bordered-tile
 * layout.
 *
 * Deliberately has NO back-link/close affordance of its own: the strip that
 * wraps this component (U14) owns "‹ Back to Actions" — see that unit's
 * brief. This component only ever renders what a single resolved request
 * looks like.
 */

export interface ActionsDetailProps {
  requestId: string;
}

/**
 * Small local copy affordance — Button (secondary) that flashes "Copied"
 * (Glaze check icon, not a unicode glyph) for `resetMs` after a successful
 * write. `getText` is evaluated lazily at click time so the two call sites
 * below (the response envelope vs. the shareable link) never share a stale
 * closure over `entry.response`.
 */
function CopyButton({ label, getText }: { label: string; getText: () => string }) {
  const [copied, flashCopied] = useCopiedFlash(1200);
  return (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => {
        void navigator.clipboard?.writeText(getText());
        flashCopied();
      }}
    >
      {copied ? (
        <>
          <Icon type={FLOW_ICONS.confirm} size="sm" aria-hidden />
          Copied
        </>
      ) : (
        label
      )}
    </Button>
  );
}

/**
 * Warning banner for a row whose expiry code is `CLAY_ROUTINE_RESULT_UNAVAILABLE`
 * — Clay no longer has an answer for this run, NOT "this genuinely failed."
 * Ported verbatim (copy + test id) from `EnrollQueue/RequestDetail.tsx`'s
 * `UnknownOutcomeNotice`.
 */
function UnknownOutcomeNotice() {
  return (
    <div
      data-testid="request-outcome-unknown-notice"
      className="rounded-md border border-border-warning bg-bg-orange-light px-3 py-2.5 text-xs text-content-warning"
    >
      <Strong className="text-xs text-content-warning">Outcome unknown.</Strong> Clay no longer has a
      result for this run — it may have completed successfully. Check for side effects (e.g. an
      enrollment or CRM update) before re-running this action.
    </div>
  );
}

/**
 * The "Send attempts" history for a play row — one line per
 * `enrollment_send_attempt`, oldest first (the endpoint sorts ascending by
 * `createdAt`). Rendered ONLY for `kind === 'workflow'`: an instant action
 * has no enrollment and therefore never has attempts. Ported verbatim
 * (copy, test id, `pending` semantics) from `RequestDetail.tsx`'s
 * `SendAttempts`.
 */
function SendAttempts({ attempts, pending }: { attempts: RequestDetailPayload['attempts']; pending: boolean }) {
  return (
    <Module fill="secondary" data-testid="request-send-attempts">
      <Module.Header>Send attempts</Module.Header>
      {pending ? (
        <Text size="xs" variant="tertiary">Loading send attempts…</Text>
      ) : attempts.length === 0 ? (
        <Text size="xs" variant="tertiary">No send attempts yet.</Text>
      ) : (
        <div className="flex flex-col gap-1.5">
          {attempts.map(attempt => (
            <div key={attempt.id} className="rounded-md border border-border-primary bg-bg-primary px-2.5 py-2">
              <div className="flex items-center gap-2">
                <RequestAttemptStatusBadge status={attempt.status} />
                <Text size="xs" variant="tertiary" className="ml-auto whitespace-nowrap">
                  {formatAbsoluteDateTime(attempt.dispatchStartedAt ?? attempt.updatedAt)}
                </Text>
              </div>
              {attempt.error && (
                <Text size="xs" variant="critical" className="mt-1 break-words">
                  {attempt.error}
                </Text>
              )}
            </div>
          ))}
        </div>
      )}
    </Module>
  );
}

/**
 * The queue-side affordances for a play row. Ported verbatim (logic, wire
 * shape, copy) from `RequestDetail.tsx`'s `EnrollmentActions` — see that
 * component's doc comment for why Re-enroll STAGES a new queue item rather
 * than mutating/resending the submitted row.
 */
function EnrollmentActions({
  enrollment,
  routeId,
}: {
  enrollment: NonNullable<RequestDetailPayload['enrollment']>;
  routeId: string;
}) {
  const { isSubmitted, canReenroll, pending, error, readOnly, onReenrollClick, onOpenQueue } = useReenroll(enrollment, routeId);

  if (!isSubmitted) {
    return (
      <div data-testid="request-enrollment-actions" className="flex flex-col items-start gap-1.5 rounded-md bg-bg-secondary px-3 py-2.5">
        <Text size="xs" variant="secondary">Still in your queue — retry from the Send button</Text>
        <Button variant="ghost" size="xs" onClick={onOpenQueue}>Open queue</Button>
      </div>
    );
  }

  if (!canReenroll) return null;

  return (
    <div data-testid="request-enrollment-actions" className="flex flex-col items-start gap-1.5">
      <Button
        variant="secondary"
        size="sm"
        disabled={readOnly || pending}
        title={readOnly ? 'Read-only while impersonating' : undefined}
        onClick={onReenrollClick}
      >
        {pending ? 'Adding to queue…' : 'Re-enroll (adds to your queue)'}
      </Button>
      {error && (
        <Text size="xs" variant="critical" data-testid="request-reenroll-error">
          {error}
        </Text>
      )}
    </div>
  );
}

/**
 * "Applied to contact" — only rendered when the completed run's
 * `appliedSnapshot` carries a `contact_field_overlay` binding. The jump only
 * navigates when the entry carries a resolvable contact id/email; a
 * contact-less run (e.g. an account-scoped action) still shows the staged
 * value with no jump button. Ported verbatim (logic, narrowing, copy) from
 * `RequestDetail.tsx`'s `AppliedToContact`.
 */
function AppliedToContact({ entry, onJump }: { entry: RequestEntry; onJump: () => void }) {
  const overlay = appliedOverlayFor(entry);
  if (!overlay) return null;

  return (
    <Module fill="secondary">
      <Module.Header controls={overlay.canJump ? <Button variant="ghost" size="xs" onClick={onJump}>View on contact →</Button> : undefined}>
        Applied to contact
      </Module.Header>
      <div className="flex flex-wrap items-center gap-2">
        <Text size="xs" variant="tertiary">{overlay.field}</Text>
        <Strong className="text-xs text-content-warning">{overlay.displayValue}</Strong>
      </div>
    </Module>
  );
}

/** Loading skeleton — three placeholder lines standing in for header + the two payload modules. */
function ActionsDetailSkeleton() {
  return (
    <div data-testid="request-detail-skeleton" aria-busy="true" className="flex flex-col gap-4">
      <SkeletonText size="lg" className="w-1/2" />
      <SkeletonText size="sm" className="w-1/3" />
      <SkeletonText size="xl" />
    </div>
  );
}

function ActionsDetailBody({ detail, pending }: { detail: RequestDetailPayload; pending: boolean }) {
  const { jumpToContact } = useEnrollQueue();
  // Pretty/Raw is a pure UI display toggle (no seed to reset from), not
  // editable content — plain useState is fine here; the "Draft State Under
  // SSE" rule only governs fields an SSE-driven parent re-render could
  // silently reseed (CLAUDE.md).
  const [mode, setMode] = useState<FactMode>('pretty');
  const entry = detail.entry;

  const who = entry.contact?.name || entry.contact?.email || entry.contact?.id || null;
  // Computed once here (rather than re-deriving at the `onJump` call site)
  // so the null-guard below is a real guard, not a cast past `undefined`.
  const contactRef = entry.contact?.id ?? entry.contact?.email;
  const isInFlight = entry.status === 'pending' || entry.status === 'running';
  const responseCode = extractResponseCode(entry.response);
  const outcomeUnavailable = isOutcomeUnavailable(entry.status, responseCode);
  const sharePath = requestPathFromFeedEntry(entry);
  // What a re-enrolled item's `source_context` names this request by — the
  // ROUTE spelling (what a rep can paste), falling back to the feed id for a
  // row with no shareable URL.
  const sharePathRouteId = routeIdFromFeedEntry(entry) ?? entry.id;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <Strong className="block truncate text-sm">{who ?? entry.actionName}</Strong>
            {who && <Text size="xs" variant="tertiary" className="truncate">{entry.actionName}</Text>}
          </div>
          <RequestStatusBadge status={entry.status} code={responseCode} />
        </div>
        <div className="flex items-center gap-2">
          <Text size="xs" variant="tertiary">{formatAbsoluteDateTime(entry.ts)}</Text>
          {sharePath && (
            <div className="ml-auto">
              <CopyButton label="Copy link" getText={() => `${window.location.origin}${sharePath}`} />
            </div>
          )}
        </div>
      </div>

      {isInFlight && (
        <Text size="xs" variant="tertiary" className="italic">
          {entry.status === 'pending' ? 'Queued — dispatching to handler…' : 'Executing action handler, streaming result…'}
        </Text>
      )}

      {outcomeUnavailable && <UnknownOutcomeNotice />}

      {entry.kind === 'workflow' && <SendAttempts attempts={detail.attempts} pending={pending} />}

      {detail.enrollment && <EnrollmentActions enrollment={detail.enrollment} routeId={sharePathRouteId} />}

      <FactModule title="Request inputs" data={entry.inputs} mode={mode} />

      {!isInFlight && (
        <>
          {/*
            Pretty mode shows ONLY the actual reply data (extractDisplayPayload
            — prefers `meta.result`, the Clay per-item result object, over the
            full `{ ok, code, message, meta }` handler_result envelope). Raw
            mode is untouched — it's the one place the envelope/metadata is
            still allowed to show, which is also why the Copy button below
            keeps copying `entry.response` unmodified regardless of which mode
            is active.
          */}
          <FactModule
            title="Response payload"
            data={mode === 'pretty' ? extractDisplayPayload(entry.response) : entry.response}
            mode={mode}
            controls={
              <>
                <SegmentedControl<FactMode> value={mode} onValueChange={setMode} size="xs">
                  <SegmentedControlOption value="pretty">Pretty</SegmentedControlOption>
                  <SegmentedControlOption value="raw">Raw</SegmentedControlOption>
                </SegmentedControl>
                <CopyButton label="Copy" getText={() => JSON.stringify(entry.response, null, 2)} />
              </>
            }
          />
          <AppliedToContact
            entry={entry}
            onJump={() => { if (contactRef) jumpToContact(contactRef); }}
          />
        </>
      )}
    </div>
  );
}

/**
 * Resolves `requestId` through the detail endpoint, then renders. Three
 * outcomes: still loading (skeleton), no such request / unloadable
 * (not-found copy), or the real detail above.
 */
export function ActionsDetail({ requestId }: ActionsDetailProps) {
  const { data, isLoading, isError, error, isPlaceholderData } = useFlowRequestDetail(requestId);

  if (isLoading) {
    return <ActionsDetailSkeleton />;
  }

  if (!data) {
    return (
      <EmptyState
        title="Request not found"
        description={
          isError
            ? (error instanceof Error ? error.message : 'Failed to load this request.')
            : 'This request no longer exists, or the link is wrong.'
        }
      />
    );
  }

  // `isPlaceholderData` = this is the list-cache first paint, not the
  // authoritative read — the sections the LIST cannot know about (attempts)
  // must say so rather than render an empty truth-claim.
  return <ActionsDetailBody detail={data} pending={isPlaceholderData} />;
}
